/**
 * Tenant API service
 */

import { apiClient } from './api-client';
import type { Tenant, TenantCreateRequest, TenantUpdateRequest, PaginationOptions, PaginatedResponse, TenantStatistics, TenantFilterOptions } from '@/types';
import type { ApiResponse } from '@/types/api';

interface TenantListResponse {
  items: any[];
  nextToken?: string;
  hasMore: boolean;
}

// Map API response fields to frontend fields
function mapTenantFromApi(apiTenant: any): Tenant {
  return {
    id: apiTenant.tenantId,
    company: apiTenant.companyName,
    creators: [{ 
      name: `${apiTenant.firstName} ${apiTenant.lastName}`,
      email: apiTenant.email 
    }],
    companySize: apiTenant.companySize,
    deploymentId: apiTenant.deployment,
    numberOfLicenses: apiTenant.quantity,
    status: apiTenant.status,
    lastModified: new Date(apiTenant.renewalDate).toLocaleDateString(),
    insertTimestamp: new Date().toLocaleDateString() + ' ' + new Date().toLocaleTimeString(), // API doesn't return this, using current time as placeholder
  };
}

export class TenantService {
  private readonly basePath = '/api/tenants';

  /**
   * Get all tenants with pagination and filtering
   */
  async getTenants(options?: PaginationOptions, filters?: TenantFilterOptions): Promise<PaginatedResponse<Tenant>> {
    const params = new URLSearchParams();
    
    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }
    if (options?.nextToken) {
      params.append('nextToken', options.nextToken);
    }
    
    if (filters?.name) {
      params.append('name', filters.name);
    }
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.minLicenses !== undefined) {
      params.append('minLicenses', filters.minLicenses.toString());
    }
    if (filters?.maxLicenses !== undefined) {
      params.append('maxLicenses', filters.maxLicenses.toString());
    }
    
    const url = params.toString() ? `${this.basePath}?${params}` : this.basePath;
    const response = await apiClient.get<ApiResponse<TenantListResponse>>(url);
    
    return {
      items: response.data?.items ? response.data.items.map(mapTenantFromApi) : [],
      nextToken: response.data?.nextToken,
      hasMore: response.data?.hasMore ?? false
    };
  }

  /**
   * Get a specific tenant by ID
   */
  async getTenant(id: string): Promise<Tenant> {
    const response = await apiClient.get<ApiResponse<any>>(`${this.basePath}/${id}`);
    return mapTenantFromApi(response.data);
  }

  /**
   * Create a new tenant
   */
  async createTenant(_data: TenantCreateRequest): Promise<Tenant> {
    throw new Error('Not Implemented');
  }

  /**
   * Update an existing tenant
   */
  async updateTenant(_id: string, _data: TenantUpdateRequest): Promise<Tenant> {
    throw new Error('Not Implemented');
  }

  /**
   * Delete a tenant
   */
  async deleteTenant(id: string): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`${this.basePath}/${id}`);
  }

  /**
   * Get tenant statistics with optional filtering
   */
  async getTenantStatistics(filters?: TenantFilterOptions): Promise<TenantStatistics> {
    const params = new URLSearchParams();
    
    if (filters?.name) {
      params.append('name', filters.name);
    }
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.minLicenses !== undefined) {
      params.append('minLicenses', filters.minLicenses.toString());
    }
    if (filters?.maxLicenses !== undefined) {
      params.append('maxLicenses', filters.maxLicenses.toString());
    }
    
    const url = params.toString() ? `${this.basePath}/statistics?${params}` : `${this.basePath}/statistics`;
    const response = await apiClient.get<ApiResponse<TenantStatistics>>(url);
    if (!response.data) {
      throw new Error('Failed to fetch tenant statistics');
    }
    return response.data;
  }

  /**
   * Recursively fetch tenants until we have at least the requested limit or no more data.
   * nextToken will be set only if there is more data to fetch for the next page.
   */
  async getTenantsAccumulate(
    options?: PaginationOptions,
    filters?: TenantFilterOptions
  ): Promise<PaginatedResponse<Tenant>> {
    const limit = options?.limit || 25;
    let accumulated: Tenant[] = [];
    let nextToken = options?.nextToken;
    let hasMore = true;
    let lastPageNextToken: string | undefined;

    while (accumulated.length < limit && hasMore) {
      const page = await this.getTenants(
        { limit: limit - accumulated.length, nextToken },
        filters
      );
      accumulated = accumulated.concat(page.items);
      lastPageNextToken = page.nextToken;
      hasMore = !!page.nextToken && page.hasMore;
      nextToken = page.nextToken;
      if (!hasMore) break;
    }

    const finalNextToken = (accumulated.length === limit && hasMore) ? lastPageNextToken : undefined;

    return {
      items: accumulated.slice(0, limit),
      nextToken: finalNextToken,
      hasMore: !!finalNextToken
    };
  }
}

export const tenantService = new TenantService();
